import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  alpha,
  useTheme,
  useMediaQuery,
  Alert,
  Button
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  PieChart as PieChartIcon,
  Assessment as AssessmentIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  AccountBalance
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { motion } from 'framer-motion';
import { useTransactions } from '../../contexts/TransactionContext';
import SpendingPatternsChart from './SpendingPatternsChart';
import CategoryBreakdownChart from './CategoryBreakdownChart';
import TrendAnalysisChart from './TrendAnalysisChart';
import SankeyFlowChart from './SankeyFlowChart';
import PageTransition from '../PageTransition';
import { formatCurrency } from '../../utils/chartUtils';
import { subMonths, startOfMonth } from 'date-fns';

type ChartView = 'overview' | 'patterns' | 'categories' | 'trends' | 'flow';
type TimeRange = 'month' | 'quarter' | 'year' | 'custom';
type TransactionType = 'all' | 'expense' | 'income' | 'savings';

const ExpenseAnalytics: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { allTransactions, getAllTransactions, loading: transactionsLoading } = useTransactions();
  
  // State for filters and view
  const [activeView, setActiveView] = useState<ChartView>('overview');
  const [timeRange, setTimeRange] = useState<TimeRange>('year');
  const [transactionType, setTransactionType] = useState<TransactionType>('all');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);

  // Filter transactions based on selected criteria
  const filteredTransactions = useMemo(() => {
    let filtered = [...allTransactions];

    // Filter by transaction type
    if (transactionType !== 'all') {
      filtered = filtered.filter(t => t.type === transactionType);
    }

    // Filter by date range
    if (timeRange === 'custom' && startDate && endDate) {
      filtered = filtered.filter(t => {
        const transactionDate = new Date(t.date);
        return transactionDate >= startDate && transactionDate <= endDate;
      });
    } else if (timeRange !== 'custom') {
      const endDateFilter = new Date();
      let startDateFilter: Date;
      
      switch (timeRange) {
        case 'month':
          startDateFilter = startOfMonth(endDateFilter);
          break;
        case 'quarter':
          startDateFilter = subMonths(startOfMonth(endDateFilter), 3);
          break;
        case 'year':
          startDateFilter = subMonths(startOfMonth(endDateFilter), 12);
          break;
        default:
          startDateFilter = subMonths(startOfMonth(endDateFilter), 12);
      }
      
      filtered = filtered.filter(t => {
        const transactionDate = new Date(t.date);
        return transactionDate >= startDateFilter && transactionDate <= endDateFilter;
      });
    }

    return filtered;
  }, [allTransactions, transactionType, timeRange, startDate, endDate]);

  // Load all transactions on component mount
  useEffect(() => {
    const loadTransactions = async () => {
      setLoading(true);
      try {
        await getAllTransactions(true); // Skip budget filter to get all transactions
      } catch (error) {
        console.error('Error loading transactions:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTransactions();
  }, [getAllTransactions]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const totalExpenses = filteredTransactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const totalIncome = filteredTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const totalSavings = filteredTransactions
      .filter(t => t.type === 'savings')
      .reduce((sum, t) => sum + t.amount, 0);

    const transactionCount = filteredTransactions.length;
    const avgTransactionAmount = transactionCount > 0 ? (totalExpenses + totalIncome + totalSavings) / transactionCount : 0;

    return {
      totalExpenses,
      totalIncome,
      totalSavings,
      netIncome: totalIncome - totalExpenses - totalSavings,
      transactionCount,
      avgTransactionAmount
    };
  }, [filteredTransactions]);

  const handleViewChange = (event: React.MouseEvent<HTMLElement>, newView: ChartView | null) => {
    if (newView !== null) {
      setActiveView(newView);
    }
  };

  const handleTimeRangeChange = (event: any) => {
    setTimeRange(event.target.value as TimeRange);
  };

  const handleTransactionTypeChange = (event: any) => {
    setTransactionType(event.target.value as TransactionType);
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      await getAllTransactions(true);
    } catch (error) {
      console.error('Error refreshing transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderChart = () => {
    const chartProps = {
      transactions: filteredTransactions,
      loading: loading || transactionsLoading
    };

    switch (activeView) {
      case 'patterns':
        return <SpendingPatternsChart {...chartProps} timeRange={timeRange === 'month' ? 'daily' : 'monthly'} />;
      case 'categories':
        return <CategoryBreakdownChart {...chartProps} transactionType={transactionType === 'all' ? 'expense' : transactionType as 'expense' | 'income' | 'savings'} />;
      case 'trends':
        return <TrendAnalysisChart {...chartProps} analysisType={timeRange === 'quarter' ? 'quarterly' : 'monthly'} />;
      case 'flow':
        return <SankeyFlowChart {...chartProps} />;
      case 'overview':
      default:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <SpendingPatternsChart {...chartProps} timeRange="monthly" />
            </Grid>
            <Grid item xs={12} lg={6}>
              <CategoryBreakdownChart {...chartProps} transactionType="expense" />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TrendAnalysisChart {...chartProps} analysisType="monthly" />
            </Grid>
            <Grid item xs={12} lg={6}>
              <SankeyFlowChart {...chartProps} />
            </Grid>
          </Grid>
        );
    }
  };

  return (
    <PageTransition>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <AnalyticsIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
              <Typography variant="h4" fontWeight="700">
                Expense Analytics
              </Typography>
            </Stack>
            <Typography variant="subtitle1" color="text.secondary">
              Visualize your spending patterns with beautiful charts and discover opportunities to save money.
            </Typography>
          </Box>

          {/* Summary Stats */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                }}
              >
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Total Income
                  </Typography>
                  <Typography variant="h6" fontWeight="600" color="success.main">
                    {formatCurrency(summaryStats.totalIncome)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)}, ${alpha(theme.palette.error.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                }}
              >
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Total Expenses
                  </Typography>
                  <Typography variant="h6" fontWeight="600" color="error.main">
                    {formatCurrency(summaryStats.totalExpenses)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)}, ${alpha(theme.palette.info.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Total Savings
                  </Typography>
                  <Typography variant="h6" fontWeight="600" color="info.main">
                    {formatCurrency(summaryStats.totalSavings)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  background: `linear-gradient(135deg, ${alpha(summaryStats.netIncome >= 0 ? theme.palette.success.main : theme.palette.error.main, 0.1)}, ${alpha(summaryStats.netIncome >= 0 ? theme.palette.success.main : theme.palette.error.main, 0.05)})`,
                  border: `1px solid ${alpha(summaryStats.netIncome >= 0 ? theme.palette.success.main : theme.palette.error.main, 0.2)}`,
                }}
              >
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Net Income
                  </Typography>
                  <Typography variant="h6" fontWeight="600" color={summaryStats.netIncome >= 0 ? 'success.main' : 'error.main'}>
                    {formatCurrency(summaryStats.netIncome)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Controls */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <FilterListIcon />
                <Typography variant="h6">
                  Filters & View Options
                </Typography>
              </Stack>
              
              <Stack direction={isMobile ? 'column' : 'row'} spacing={2} alignItems="stretch">
                {/* View Toggle */}
                <ToggleButtonGroup
                  value={activeView}
                  exclusive
                  onChange={handleViewChange}
                  size="small"
                  sx={{ minWidth: 'fit-content' }}
                >
                  <ToggleButton value="overview">
                    <AnalyticsIcon sx={{ mr: 1 }} />
                    Overview
                  </ToggleButton>
                  <ToggleButton value="patterns">
                    <TrendingUpIcon sx={{ mr: 1 }} />
                    Patterns
                  </ToggleButton>
                  <ToggleButton value="categories">
                    <PieChartIcon sx={{ mr: 1 }} />
                    Categories
                  </ToggleButton>
                  <ToggleButton value="trends">
                    <AssessmentIcon sx={{ mr: 1 }} />
                    Trends
                  </ToggleButton>
                  <ToggleButton value="flow">
                    <AccountBalance sx={{ mr: 1 }} />
                    Flow
                  </ToggleButton>
                </ToggleButtonGroup>

                {/* Time Range */}
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Time Range</InputLabel>
                  <Select
                    value={timeRange}
                    label="Time Range"
                    onChange={handleTimeRangeChange}
                  >
                    <MenuItem value="month">This Month</MenuItem>
                    <MenuItem value="quarter">Last 3 Months</MenuItem>
                    <MenuItem value="year">Last Year</MenuItem>
                    <MenuItem value="custom">Custom Range</MenuItem>
                  </Select>
                </FormControl>

                {/* Transaction Type */}
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={transactionType}
                    label="Type"
                    onChange={handleTransactionTypeChange}
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="expense">Expenses</MenuItem>
                    <MenuItem value="income">Income</MenuItem>
                    <MenuItem value="savings">Savings</MenuItem>
                  </Select>
                </FormControl>

                {/* Custom Date Range */}
                {timeRange === 'custom' && (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={(newValue) => setStartDate(newValue)}
                      slotProps={{ textField: { size: 'small' } }}
                    />
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={(newValue) => setEndDate(newValue)}
                      slotProps={{ textField: { size: 'small' } }}
                    />
                  </LocalizationProvider>
                )}

                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={loading}
                  size="small"
                >
                  Refresh
                </Button>
              </Stack>

              {/* Active Filters */}
              <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                <Chip
                  label={`${filteredTransactions.length} transactions`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                {transactionType !== 'all' && (
                  <Chip
                    label={`Type: ${transactionType}`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                )}
                {timeRange !== 'year' && (
                  <Chip
                    label={`Range: ${timeRange}`}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Charts */}
          {filteredTransactions.length === 0 && !loading ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              No transactions found for the selected criteria. Try adjusting your filters.
            </Alert>
          ) : (
            <motion.div
              key={activeView}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              {renderChart()}
            </motion.div>
          )}
        </motion.div>
      </Container>
    </PageTransition>
  );
};

export default ExpenseAnalytics; 